import {
    Button,
    Card,
    CardContent,
    CardMedia,
    Container,
    Dialog,
    DialogActions,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { keyframes, useTheme } from '@mui/system';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import {
    useNetworkingBinaryTree,
    useNetworkingOrganization,
    useNetworkingRanks,
} from '../state/NetworkingContext';
import DashboardCard from './DashboardCard';
import DialogContent from '@mui/material/DialogContent';
import QRCode from 'react-qr-code';
import DownloadIcon from '@mui/icons-material/Download';
/*import { HowToReg as HowToRegIcon } from '@mui/icons-material';*/
import Logo from '../../../logos/FiyrPodBlackBG.png';
import CoachQSG from '../../../logos/CoachQSGCoverImage.png';
import AthleteQSG from '../../../logos/AthleteQSGCoverImage.png';
import LogoWB from '../../../logos/FiyrPodLogoWBG.png';
import FiyrPodLogo from '../../../logos/FiyrPodLogoWBG.png';
/*import FoundersSummitPage1 from '../../../logos/FoundersSummitPage1.png';
import FoundersSummitPage2 from '../../../logos/FoundersSummitPage2.png';*/
import { convertOrganizationLevelToLevel, convertRankLevelToRank } from '../networking.functions';
import { getOrgDashboardMetrics } from '../networking.api';
import { AdvancedOrganization } from '../networking.types';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const shimmerAnimation = keyframes`
    0% {
        background-position: 0% 0%;
    }
    100% {
        background-position: 200% 200%;
    }
`;

const PartnershipDashboardScene = () => {
    const theme = useTheme();
    const binaryTree = useNetworkingBinaryTree();
    const organization = useNetworkingOrganization();
    const [organizationData, setOrganizationData] = useState<AdvancedOrganization>();
    const [data, setData] = useState<{ title: string; value: number | string }[]>([]);
    const [openQR, setOpenQR] = React.useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const ranksData = useNetworkingRanks();
    const slug = organization.data?.slug;
    const fiyrpodStoreUrl = slug ? `https://${slug}.fiyrpod.com` : '';

    const handleClickOpenQR = () => {
        setOpenQR(true);
    };

    const handleCloseQR = () => {
        setOpenQR(false);
    };

    const copyToClipboard = () => {
        if (fiyrpodStoreUrl) {
            navigator.clipboard.writeText(fiyrpodStoreUrl);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = Logo; // Ensure Logo is a valid path to the image file
        link.download = 'logo.png'; // The filename for the downloaded image
        link.click();
    };

    useEffect(() => {
        getOrgDashboardMetrics(organization.data?.uuid as string, {
            organization_uuid: organization.data?.uuid,
        })
            .then((response) => {
                setOrganizationData(response.data.data[0]);
            })
            .catch((error) => {
                console.error('Failed to fetch organizations', error);
            });
    }, [organization]);

    useEffect(() => {
        if (!binaryTree.data || !organizationData) {
            setData([]);
            return;
        }
        const d = [
            {
                title: 'Current Rank',
                value: convertRankLevelToRank(binaryTree.data.rank, ranksData.data || []),
            },
            { title: 'Current Leadership Rank', value: binaryTree.data.leadership_rank },
            {
                title: 'Marketplace Sales',
                value: `$${((binaryTree.data.levels?.marketplaceSales || 0) / 100).toFixed(2)}`,
            },
            { title: 'Paid Athlete Count', value: organizationData.athlete_count || 0 },
            {
                title: 'Organization Subscription Level',
                value: convertOrganizationLevelToLevel(
                    binaryTree.data?.levels?.organizationSubscriptionLevel || 0,
                ),
            },
        ];
        setData(d);
    }, [binaryTree.data, ranksData.data, organizationData]);

    return (
        <Container
            sx={{
                flex: 1,
            }}
            maxWidth={'xl'}
        >
            <Paper
                sx={{ margin: 6, background: 'linear-gradient(to bottom, white 30%, #EDF1F8 70%)' }}
            >
                <Box sx={{ width: '100%' }}>
                    <Typography sx={{ mt: 10, mb: 10 }} textAlign="center" variant="h6">
                        Your Organization Status and Rank
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 4,
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(3, 1fr)',
                                md: 'repeat(5, 1fr)',
                            },
                            mt: 2,
                        }}
                    >
                        {data.length > 0 ? (
                            data.map((item) => (
                                <DashboardCard
                                    key={item.title}
                                    title={item.title}
                                    value={item.value}
                                />
                            ))
                        ) : (
                            <Typography
                                sx={{
                                    gridColumn: '1 / -1',
                                    textAlign: 'center',
                                    color: 'text.secondary',
                                    py: 4,
                                }}
                            >
                                Loading organization data...
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>
            <Grid container justifyContent="center" sx={{ marginTop: theme.spacing(6) }}>
                <Grid item xs={12} md={10} lg={8}>
                    <Paper
                        elevation={8}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            marginTop: theme.spacing(6),
                            padding: theme.spacing(4),
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.light}08 100%)`,
                            border: `2px solid ${theme.palette.primary.main}40`,
                            borderRadius: 3,
                            position: 'relative',
                            overflow: 'visible',
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                textAlign: 'center',
                                padding: theme.spacing(2, 3),
                                fontWeight: 'bold',
                                color: theme.palette.primary.main,
                                mb: 2,
                            }}
                        >
                            🔗 Share My Signup and Order Link!
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                textAlign: 'center',
                                padding: theme.spacing(0, 3, 3, 3),
                                color: 'text.secondary',
                                mb: 3,
                            }}
                        >
                            Copy and share this link with athletes and coaches to get started and
                            place orders
                        </Typography>
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: '800px',
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                alignItems: 'center',
                            }}
                        >
                            <TextField
                                label="Fiyrpod Store URL"
                                variant="outlined"
                                value={
                                    slug
                                        ? fiyrpodStoreUrl
                                        : 'Set a custom URL slug in Organization Details to generate your store link'
                                }
                                InputProps={{
                                    readOnly: true,
                                    sx: {
                                        fontSize: '1.1rem',
                                        fontWeight: 500,
                                    },
                                    endAdornment: slug ? (
                                        <InputAdornment position="end">
                                            <Tooltip title={copySuccess ? 'Copied!' : 'Copy URL'}>
                                                <IconButton
                                                    aria-label="copy fiyrpod url"
                                                    onClick={copyToClipboard}
                                                    edge="end"
                                                    size="large"
                                                    sx={{
                                                        color: theme.palette.primary.main,
                                                        '&:hover': {
                                                            backgroundColor: `${theme.palette.primary.main}15`,
                                                        },
                                                    }}
                                                >
                                                    <ContentCopyIcon fontSize="large" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ) : undefined,
                                }}
                                sx={{
                                    flex: 1,
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '1.1rem',
                                        backgroundColor: 'white',
                                        '&:hover': {
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    },
                                }}
                            />
                            {slug && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        width: { xs: '100%', sm: 'auto' },
                                        flexDirection: { xs: 'column', sm: 'row' },
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="large"
                                        onClick={() => {
                                            window.open(
                                                fiyrpodStoreUrl,
                                                '_blank',
                                                'noopener,noreferrer',
                                            );
                                        }}
                                        sx={{
                                            minWidth: { xs: '100%', sm: '200px' },
                                            height: '56px',
                                            fontSize: '1rem',
                                            fontWeight: 'bold',
                                            textTransform: 'none',
                                            boxShadow: 3,
                                            '&:hover': {
                                                boxShadow: 6,
                                            },
                                        }}
                                    >
                                        Open Store
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        startIcon={<QrCode2Icon />}
                                        onClick={handleClickOpenQR}
                                        sx={{
                                            minWidth: { xs: '100%', sm: '200px' },
                                            height: '56px',
                                            fontSize: '1rem',
                                            fontWeight: 'bold',
                                            textTransform: 'none',
                                            borderColor: '#000',
                                            color: '#000',
                                            '&:hover': {
                                                borderColor: '#000',
                                                backgroundColor: '#0000000a',
                                            },
                                        }}
                                    >
                                        QR Code
                                    </Button>
                                </Box>
                            )}
                        </Box>
                        {!slug && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    textAlign: 'center',
                                    padding: theme.spacing(2, 3, 0, 3),
                                    fontStyle: 'italic',
                                }}
                            >
                                💡 Set a custom URL slug in Organization Details and save to
                                generate your store link
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* QR Code Modal */}
            <Dialog
                open={openQR}
                onClose={handleCloseQR}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#0a0a0a',
                        color: '#FFFFFF',
                        borderRadius: 3,
                        position: 'relative',
                        overflow: 'hidden',
                        background: `
                            radial-gradient(ellipse at top left, rgba(0, 255, 0, 0.1) 0%, transparent 50%),
                            radial-gradient(ellipse at bottom right, rgba(0, 150, 255, 0.1) 0%, transparent 50%),
                            radial-gradient(ellipse at top right, rgba(150, 0, 255, 0.1) 0%, transparent 50%),
                            #0a0a0a
                        `,
                        border: '4px solid',
                        borderImage: `
                            linear-gradient(
                                135deg,
                                #00FF00 0%,
                                #00CCFF 25%,
                                #9900FF 50%,
                                #FF0099 75%,
                                #00FF00 100%
                            ) 1
                        `,
                        boxShadow: `
                            0 0 30px rgba(0, 255, 0, 0.3),
                            inset 0 0 30px rgba(0, 255, 0, 0.05)
                        `,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `
                                linear-gradient(45deg, transparent 30%, rgba(0, 255, 0, 0.05) 50%, transparent 70%),
                                linear-gradient(-45deg, transparent 30%, rgba(0, 150, 255, 0.05) 50%, transparent 70%),
                                linear-gradient(135deg, transparent 30%, rgba(150, 0, 255, 0.05) 50%, transparent 70%)
                            `,
                            backgroundSize: '200% 200%',
                            animation: `${shimmerAnimation} 3s ease-in-out infinite`,
                            pointerEvents: 'none',
                            zIndex: 0,
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <DialogTitle
                        sx={{
                            textAlign: 'center',
                            padding: theme.spacing(2, 3, 1, 3),
                            borderBottom: 'none',
                            background: 'transparent',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Box
                            component="img"
                            src={FiyrPodLogo}
                            alt="FIYRPOD"
                            sx={{
                                height: '40px',
                                width: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                    </DialogTitle>
                    <Box
                        sx={{
                            textAlign: 'center',
                            padding: theme.spacing(0, 3, 2, 3),
                        }}
                    >
                        <Box
                            sx={{
                                display: 'inline-block',
                                padding: theme.spacing(1, 3),
                                borderRadius: 1,
                                border: '2px solid #00FF00',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                backdropFilter: 'blur(10px)',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '1.2rem',
                                    fontWeight: 'bold',
                                    color: '#FFFFFF',
                                    textTransform: 'uppercase',
                                    letterSpacing: '1px',
                                }}
                            >
                                {organization.data?.name || 'Organization'}
                            </Typography>
                        </Box>
                    </Box>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            padding: theme.spacing(1, 3, 2, 3),
                            fontSize: '0.9rem',
                            fontWeight: 'normal',
                            color: '#FFFFFF',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                        }}
                    >
                        SCAN TO JOIN
                    </Typography>
                    <DialogContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: theme.spacing(2, 4, 2, 4),
                            background: 'transparent',
                        }}
                    >
                        <Box
                            sx={{
                                backgroundColor: '#FFFFFF',
                                padding: theme.spacing(3),
                                borderRadius: 2,
                                marginBottom: theme.spacing(2),
                                boxShadow: '0 0 30px rgba(0, 255, 0, 0.6)',
                                border: '3px solid #00FF00',
                            }}
                        >
                            <QRCode
                                size={256}
                                style={{
                                    height: 'auto',
                                    maxWidth: '100%',
                                    width: '100%',
                                }}
                                value={fiyrpodStoreUrl}
                                viewBox="0 0 256 256"
                            />
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                textAlign: 'center',
                                color: '#00FF00',
                                fontWeight: 'bold',
                                marginTop: theme.spacing(1),
                                textShadow: '0 0 15px rgba(0, 255, 0, 0.8)',
                                fontSize: '1rem',
                            }}
                        >
                            Signup and Order Link
                        </Typography>
                    </DialogContent>
                    <DialogActions
                        sx={{
                            justifyContent: 'center',
                            padding: theme.spacing(2, 3, 3, 3),
                            borderTop: 'none',
                            background: 'transparent',
                        }}
                    >
                        <Button
                            onClick={handleCloseQR}
                            variant="outlined"
                            sx={{
                                borderColor: '#00FF00',
                                borderWidth: '2px',
                                color: '#FFFFFF',
                                fontWeight: 'bold',
                                padding: theme.spacing(1.5, 4),
                                textTransform: 'uppercase',
                                letterSpacing: '2px',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                backdropFilter: 'blur(10px)',
                                boxShadow: '0 0 15px rgba(0, 255, 0, 0.3)',
                                '&:hover': {
                                    borderColor: '#00FF00',
                                    backgroundColor: 'rgba(0, 255, 0, 0.1)',
                                    boxShadow: '0 0 20px rgba(0, 255, 0, 0.5)',
                                },
                            }}
                        >
                            LET&apos;S GO
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
            <Paper
                sx={{ margin: 6, background: 'linear-gradient(to bottom, white 30%, #EDF1F8 70%)' }}
            >
                <Box>
                    <Grid container alignItems="center" justifyContent="center">
                        <Grid container direction="column" alignItems="center">
                            <Grid item>
                                <Typography sx={{ mt: 10, mb: 10 }} textAlign="center" variant="h6">
                                    Brand Partner Policy and Pod Lease Agreements
                                </Typography>
                                <Typography
                                    sx={{ marginTop: 20, marginBottom: 20 }}
                                    variant="subtitle1"
                                >
                                    Below you will find the policies for Brand Partners,
                                    Organization Leases, and Athlete Leases
                                </Typography>
                                <Grid item textAlign="center">
                                    <Typography
                                        sx={{ marginTop: 20, marginBottom: 20 }}
                                        variant="subtitle2"
                                    >
                                        <a
                                            href="https://docs.google.com/document/d/1SUEM58W05ou92tIIB-TNmWJPI_Ail5IZAWCOGQG7ZwI/edit?usp=sharing"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Brand Partner Agreement
                                        </a>
                                    </Typography>
                                </Grid>
                                <Grid item textAlign="center">
                                    <Typography
                                        sx={{ marginTop: 20, marginBottom: 20 }}
                                        variant="subtitle2"
                                    >
                                        <a
                                            href="https://docs.google.com/document/d/19vs8lZqZkkotR4cAU9lRMY6QFyil7_mgiChMyIzVmcE/edit?usp=sharing"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Organization Pod Lease Agreement
                                        </a>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} textAlign="center">
                                    <Typography
                                        sx={{ marginTop: 20, marginBottom: 20 }}
                                        variant="subtitle2"
                                    >
                                        <a
                                            href="https://docs.google.com/document/d/1QjR9EqY6SxJe9snTI7g1hGdnC6OG8wc0qt0bXua5VR4/edit?usp=sharing"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Athlete Pod Lease Agreement
                                        </a>
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
            <Paper
                sx={{ margin: 6, background: 'linear-gradient(to bottom, white 30%, #EDF1F8 70%)' }}
            >
                <Grid container direction="column" alignItems="center" justifyContent="center">
                    <Grid item xs={12}>
                        <Typography sx={{ mt: 10, mb: 10 }} textAlign="center" variant="h6">
                            Marketing Resources
                        </Typography>
                        <Typography
                            sx={{ marginTop: 20, marginBottom: 20 }}
                            textAlign="center"
                            variant="subtitle1"
                        >
                            Below you will find the marketing resources and Athlete and Coach Quick
                            Start Guides for
                            <strong> FiyrPod 🔥🔥🔥 </strong>
                        </Typography>
                    </Grid>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <Grid
                            container
                            direction="row"
                            sx={{
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{ display: 'flex', justifyContent: 'center' }}
                            >
                                <Card
                                    sx={{
                                        maxWidth: 345,
                                        width: '100%',
                                        boxShadow: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        padding: 2,
                                        background:
                                            'linear-gradient(to bottom, black 30%, #EDF1F8 70%)',
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        image={Logo}
                                        alt="Logo"
                                        sx={{
                                            height: 140,
                                            width: 140,
                                            objectFit: 'contain',
                                            marginBottom: 2,
                                        }}
                                    />
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Typography variant={'h6'}>Black Background</Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownload}
                                            sx={{ marginTop: 2 }}
                                        >
                                            Save Logo
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    display: 'flex',
                                }}
                            >
                                <Card
                                    sx={{
                                        maxWidth: 345,
                                        width: '100%',
                                        boxShadow: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        padding: 2,
                                        background:
                                            'linear-gradient(to bottom, white 30%, #EDF1F8 70%)',
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        image={LogoWB}
                                        alt="Logo"
                                        sx={{
                                            height: 140,
                                            width: 140,
                                            objectFit: 'contain',
                                            marginBottom: 2,
                                        }}
                                    />
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Typography variant={'h6'}>White Background</Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownload}
                                            sx={{ marginTop: 2 }}
                                        >
                                            Save Logo
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    display: 'flex',
                                }}
                            >
                                <Card
                                    sx={{
                                        maxWidth: 345,
                                        width: '100%',
                                        boxShadow: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        padding: 2,
                                        background:
                                            'linear-gradient(to bottom, white 30%, #EDF1F8 70%)',
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        image={CoachQSG}
                                        alt="Logo"
                                        sx={{
                                            maxHeight: 250,
                                            width: 'auto',
                                            objectFit: 'contain',
                                            marginBottom: 2,
                                        }}
                                    />
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<OpenInNewIcon />}
                                            onClick={() =>
                                                window.open(
                                                    'https://fiyrpod.hflip.co/coach-quick-start-guide.html',
                                                    '_blank',
                                                )
                                            }
                                            sx={{ marginTop: 2 }}
                                        >
                                            Visit Coach Quick Start Guide
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    display: 'flex',
                                }}
                            >
                                <Card
                                    sx={{
                                        maxWidth: 345,
                                        width: '100%',
                                        boxShadow: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        padding: 2,
                                        background:
                                            'linear-gradient(to bottom, white 30%, #EDF1F8 70%)',
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        image={AthleteQSG}
                                        alt="Logo"
                                        sx={{
                                            maxHeight: 250,
                                            width: 'auto',
                                            objectFit: 'contain',
                                            marginBottom: 2,
                                        }}
                                    />
                                    <CardContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<OpenInNewIcon />}
                                            onClick={() =>
                                                window.open(
                                                    'https://fiyrpod.hflip.co/athlete-quick-start-guide.html',
                                                    '_blank',
                                                )
                                            }
                                            sx={{ marginTop: 2 }}
                                        >
                                            Visit Athlete Quick Start Guide
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                    {/*<Grid container spacing={2} justifyContent="center" alignItems="center">
                        <Grid item xs={12} md={11}>
                            <Card
                                sx={{
                                    maxWidth: 'auto',
                                    width: '100%',
                                    boxShadow: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    padding: { xs: 1, sm: 2 },
                                    marginTop: 6,
                                    background:
                                        'linear-gradient(to bottom, white 30%, #EDF1F8 70%)',
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    image={FoundersSummitPage1}
                                    alt="Logo"
                                    sx={{
                                        height: 'auto',
                                        width: '100%',
                                        objectFit: 'cover',
                                        marginBottom: 2,
                                    }}
                                />
                                <CardContent
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography variant={'h6'}>FiyrPod Summit</Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<HowToRegIcon />}
                                        onClick={() =>
                                            window.open(
                                                'https://www.eventbrite.com/e/fiyrpod-founders-summit-tickets-1043302454727?aff=oddtdtcreator',
                                                '_blank',
                                                'noopener,noreferrer',
                                            )
                                        }
                                        sx={{
                                            marginTop: 2,
                                            backgroundColor: '#05E354',
                                            '&:hover': {
                                                backgroundColor: '#04B843', // A slightly darker shade for hover effect
                                            },
                                        }}
                                    >
                                        Register Now
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={11}>
                            <Card
                                sx={{
                                    maxWidth: 'auto',
                                    width: '100%',
                                    boxShadow: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    padding: { xs: 1, sm: 2 },
                                    marginTop: 6,
                                    background:
                                        'linear-gradient(to bottom, white 30%, #EDF1F8 70%)',
                                }}
                            >
                                <CardMedia
                                    component="img"
                                    image={FoundersSummitPage2}
                                    alt="Logo"
                                    sx={{
                                        height: 'auto',
                                        width: '100%',
                                        objectFit: 'cover',
                                        marginBottom: 2,
                                    }}
                                />
                                <CardContent
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography variant={'h6'}>FiyrPod Summit</Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<HowToRegIcon />}
                                        onClick={() =>
                                            window.open(
                                                'https://www.eventbrite.com/e/fiyrpod-founders-summit-tickets-1043302454727?aff=oddtdtcreator',
                                                '_blank',
                                                'noopener,noreferrer',
                                            )
                                        }
                                        sx={{
                                            marginTop: 2,
                                            backgroundColor: '#05E354',
                                            '&:hover': {
                                                backgroundColor: '#04B843', // A slightly darker shade for hover effect
                                            },
                                        }}
                                    >
                                        Register Now
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>*/}
                </Grid>
            </Paper>
        </Container>
    );
};

export default PartnershipDashboardScene;
